import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/* `useGrouping` non è un vezzo: in italiano il raggruppamento predefinito
   ('auto') salta i numeri di quattro cifre, perché il CLDR chiede almeno due
   cifre prima del separatore. Risultato misurato: nella stessa colonna si
   leggeva "4800" e "12.500", e nella stessa fascia "2175" e "26.102".
   Si passa `true`, non la stringa 'always', perché il `lib` di TypeScript è
   fermo a ES2022 e non conosce ancora le stringhe di Intl v3; a runtime `true`
   viene normalizzato proprio in 'always' (verificato con resolvedOptions()).
   Da cinque cifre in su non cambia niente. */
const eur = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0, useGrouping: true });
const eurFull = new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true });

export function fmtEur(n: number | string | null | undefined, full = false): string {
  if (n == null || n === '') return '—';
  const num = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(num)) return '—';
  return full ? eurFull.format(num) : eur.format(num);
}

/**
 * "€ 1.234" con uno spazio che non va a capo: l'euro non resta mai da solo in
 * fondo alla riga con la cifra sotto (giro di controllo del 26/10/2026, "€ 37.844"
 * spezzato in Oggi, nella scheda cliente e nei Professionisti).
 * Il segno va davanti all'euro: "−€ 74.728". Senza numero resta il trattino.
 */
export function euro(n: number | string | null | undefined, full = false): string {
  if (n == null || n === '') return '—';
  const num = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(num)) return '—';
  const cifra = fmtEur(Math.abs(num), full);
  return `${num < 0 ? '−' : ''}€ ${cifra}`;
}

const pct0 = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 });
const pct1 = new Intl.NumberFormat('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * "12,5%": la percentuale all'italiana, con la virgola (prima "12.5%" col punto
 * su Report, scheda cliente e Talent). `decimali` 0 o 1. Senza numero, il trattino.
 */
export function fmtPct(n: number | string | null | undefined, decimali: 0 | 1 = 0): string {
  if (n == null || n === '') return '—';
  const num = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(num)) return '—';
  return `${(decimali === 1 ? pct1 : pct0).format(num)}%`;
}

export function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('it-IT', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export function fmtDateShort(d: string | Date | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}

export function initialsOf(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

/**
 * Le iniziali di un nome per una tessera: "Alab di Roberto Alessi" → "AR",
 * "AMA PROPERTIES SRL" → "AP", "GVF S.r.l." → "GV". Si saltano le forme
 * societarie e le parole piccole ("di", "e", "la"), come fa `iniziali()` del
 * telefono: prima "Alab di..." diventava "AD" e "Ama Properties SRL" "AP" per caso.
 */
export function nameInitials(name: string): string {
  const parole = name
    .replace(/\b(s\.?r\.?l\.?s?|s\.?p\.?a\.?|s\.?n\.?c\.?|s\.?a\.?s\.?|s\.?s\.?)(?=\s|$|\))/gi, ' ')
    .replace(/\b(di|e|il|la|lo|gli|le|del|della|dei|da|dal|dott|dott\.ssa|ing|avv)\b\.?/gi, ' ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (!parole.length) return name.trim().slice(0, 2).toUpperCase() || '·';
  const due = parole.length === 1 ? parole[0]!.slice(0, 2) : parole[0]!.charAt(0) + parole[1]!.charAt(0);
  return due.toUpperCase();
}

export function calcMargin(revenue: number, cost: number) {
  const abs = revenue - cost;
  const pct = revenue > 0 ? +((abs / revenue) * 100).toFixed(2) : 0;
  return { abs, pct };
}
